import { useState } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import { Box, InputBase } from '@mui/material';
import { tokens } from '../../theme';
import type { SearchFieldProps } from './types';

/**
 * The search box in the AppBar.
 *
 * **Enter submits; nothing happens per keystroke.** The field sits in the
 * *global* bar, so a live version navigates away from whatever page you were
 * reading on the first character typed — and it keeps a search to one history
 * entry rather than sixteen.
 *
 * **The typed value and the submitted value are different things.** The prop is
 * what the results show, the local draft is what is in the box, and they
 * resynchronise when the prop changes underneath — a shared link, or back.
 *
 * The focus ring is the design's red border rather than the app's blue
 * `:focus-visible` outline, which would draw inside the pill as a second box.
 */
export const SearchField = ({ value, onSubmit, label = 'Search the toolkit' }: SearchFieldProps) => {
  const [draft, setDraft] = useState(value);

  // Adjusted during render rather than in an effect: an effect renders the stale
  // value once and then corrects it, which flashes the previous query.
  const [lastValue, setLastValue] = useState(value);

  if (value !== lastValue) {
    setLastValue(value);
    setDraft(value);
  }

  return (
    <Box
      component="form"
      role="search"
      onSubmit={(event: React.FormEvent) => {
        event.preventDefault();
        onSubmit(draft.trim());
      }}
      // The callback form because the glow is a colour inside a template
      // string, which `sx` cannot resolve from a palette path.
      sx={(theme) => ({
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        width: '100%',
        maxWidth: 300,
        height: 36,
        paddingInline: 4,
        borderRadius: `${String(tokens.radius.full)}px`,
        border: 1,
        borderColor: 'border.default',
        backgroundColor: 'surface.page',
        transition: 'border-color 150ms ease-out, box-shadow 150ms ease-out',
        '&:focus-within': {
          borderColor: 'primary.main',
          boxShadow: `0 0 0 3px ${theme.palette.accent.brandSurface}`,
        },
        // The wrapper carries the focus indication; this would be a ring inside
        // a ring.
        '& input:focus-visible': { outline: 'none' },
      })}
    >
      <SearchIcon aria-hidden fontSize="small" sx={{ color: 'text.meta', flexShrink: 0 }} />

      <InputBase
        value={draft}
        onChange={(event) => {
          setDraft(event.target.value);
        }}
        // Gives mobile keyboards a Search key rather than a return key.
        type="search"
        inputProps={{ 'aria-label': label }}
        placeholder={label}
        sx={{
          flex: 1,
          fontSize: tokens.fontSize.bodySm,
          color: 'text.primary',
          // Safari draws its own clear button on type="search", matching
          // nothing else on the bar.
          '& input::-webkit-search-decoration, & input::-webkit-search-cancel-button': {
            display: 'none',
          },
        }}
      />
    </Box>
  );
};
